import { Link, useNavigate } from 'react-router-dom';
import {
  CreateScene,
  EntityScene,
  ModelScene,
  IndexScene,
  UpdateScene,
  useEntity,
  EntityAction,
  EntityFormActions,
} from 'components';
import permissions from 'modules/supplier/vendor/permissions';
import type { ListVendor, Vendor } from 'modules/supplier/vendor/types';
import { VendorNav } from './Nav';
import { Form } from './Form';
import { PriceLists } from './PriceLists';
import { Alert, Box, TableColumns } from '@ff-it/ui';
import { TruncatedList } from 'components/ui/TruncatedList';
import { VendorOutlets } from './VendorOutlets';
import { VendorFactors } from './VendorFactors';
import { ImportExportActions } from 'components/ListActions';
import { Icon } from 'components/Icon';
import { ReactElement } from 'react';
import { openCatalogAction } from 'components/CatalogAction';
import { OutletOrServiceCategoryTitle } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryTitle';

const columns: TableColumns<ListVendor> = [
  {
    content: 'Name',
    key: 'name',
    render: ({ id, name }: Vendor) => <Link to={`${id}/outlets`}>{name}</Link>,
    width: '250px',
  },
  {
    content: 'Outlets',
    key: 'outlets',
    cellProps: {
      className: 'py-0',
    },
    render: ({ id, outlets }: ListVendor) => (
      <TruncatedList
        limit={3}
        items={outlets}
        getKey={(o) => o.id}
        renderItem={(o) => (
          <Link to={`${id}/outlets/${o.id}/products`}>
            <OutletOrServiceCategoryTitle {...o} />
          </Link>
        )}
      />
    ),
  },
];

const initialValues = {
  name: '',
};

const model = {
  endpoint: 'supplier/vendors/',
  title: 'Media vendor',
  pluralTitle: 'Media vendors',
  entityTitle: ({ name }: Vendor) => name,
  permissions,
};

function VendorActions(): ReactElement {
  const ctx = useEntity();
  const navigate = useNavigate();

  const actions: EntityAction<any>[] = [
    {
      action: 'catalog',
      permKey: 'change',
      button: {
        icon: 'list-tree',
        children: 'Catalog',
        variant: 'secondary',
        size: 'sm',
        onClick: () =>
          openCatalogAction(ctx, (s) => {
            // dismissed
            if (!s) {
              return;
            }

            if (s.action === 'REMOVE' && !s.cascade) {
              navigate(`../`);
            }

            throw new Error(`Action not handled: ${s.action}`);
          }),
      },
    },
  ];

  return <EntityFormActions ml="5" actions={actions} />;
}

export const route = {
  path: 'vendors',
  element: <ModelScene {...model} />,
  children: [
    {
      index: true,
      element: (
        <IndexScene
          columns={columns}
          toolbar={
            <ImportExportActions
              import={{
                permission: permissions.import,
                endpoint: `${model.endpoint}import_xls/`,
                dialogChildren: (
                  <Alert variant="warning" className="py-0 px-1">
                    <Box fontSize="xs">
                      Upserts products, outlets and vendors by names, kind and type. <br />
                      <Icon icon="triangle-exclamation" /> Updates base gross price for existing
                    </Box>
                  </Alert>
                ),
              }}
              export={{
                permission: permissions.export,
                endpoint: `${model.endpoint}export_xls/`,
              }}
            />
          }
        />
      ),
    },
    {
      path: 'create',
      element: (
        <CreateScene initialValues={initialValues} successPath="../:id/outlets">
          <Form />
        </CreateScene>
      ),
    },
    {
      path: ':id/',
      element: (
        <EntityScene>
          <VendorNav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: (
            <UpdateScene viewDisabled heading={null} deletePath={null} actions={<VendorActions />}>
              <Form />
            </UpdateScene>
          ),
        },
        {
          path: 'outlets/*',
          element: <VendorOutlets />,
        },
        {
          path: 'factors/*',
          element: <VendorFactors />,
        },
        {
          path: 'price_lists/*',
          element: <PriceLists />,
        },
      ],
    },
  ],
};
